import { useEffect, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { AdminSession } from '../admin/AdminShell'
import { resolveApiBaseUrl } from '../../lib/api-base-url'
import { catalogLabel } from '../../lib/catalog-label'
import { useEscapeClose } from '../../lib/use-escape-close'

type DrawerMode = 'create' | 'edit' | 'view'
type SortKey = 'slug' | 'label' | 'status' | 'updatedAt' | 'deletedAt'

type RuleItem = {
  id: string
  slug: string
  label: string
  description: string
  status: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  deletedBy: {
    id: string
    name: string
    email: string
  } | null
  roles?: Array<{
    slug: string
    label: string
  }>
  rolesCount: number
}

type RulesPayload = {
  items: RuleItem[]
  stats: {
    total: number
    active: number
    inactive: number
    trash: number
    roles: number
  }
  catalog: {
    statuses: string[]
  }
}

type PermissionRelation = {
  roleSlug: string
  roleLabel: string
  permissionSlug: string
  permissionLabel: string
  createdAt: string
}

type PermissionSchemaTable = {
  name: string
  kind: string
  relation: string
}

type PermissionMatrixPayload = {
  roles: Array<{
    slug: string
    label: string
    permissions: string[]
    rules: string[]
  }>
  permissions: RuleItem[]
  relations: PermissionRelation[]
  schema: {
    tables: PermissionSchemaTable[]
  }
  catalog: {
    actions: string[]
    statuses: string[]
  }
}

type RuleForm = {
  slug: string
  label: string
  description: string
  status: string
}

type PageTone = 'idle' | 'success' | 'error' | 'warning'
type MenuDirection = 'up' | 'down'
type OpenMenuState = {
  id: string
  direction: MenuDirection
  coords: {
    top: number
    left: number
    right: number
    bottom: number
  }
} | null

type RulesPageProps = {
  session: AdminSession
  resource?: 'rules' | 'permissions'
}

type PermissionMatrixRole = {
  slug: string
  label: string
  permissions: string[]
  rules: string[]
}

type RolePermissionModalState = {
  role: PermissionMatrixRole
  selectedPermissionSlugs: string[]
} | null

const apiBaseUrl = resolveApiBaseUrl()

const emptyForm: RuleForm = {
  slug: '',
  label: '',
  description: '',
  status: 'active',
}

const ROWS_PER_PAGE = 10

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="9" cy="12" r="3" />
      <path d="M12 12h8" />
      <path d="M18 12v3" />
      <path d="M16 12v2" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 7h16" />
      <path d="M9 4h6" />
      <path d="M8 7l1 13h6l1-13" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  )
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m4 20 4.5-1 10-10-3.5-3.5-10 10Z" />
      <path d="m13 6 3.5 3.5" />
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 7h16" />
      <path d="M9 4h6" />
      <path d="M8 7l1 13h6l1-13" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m5 12 4 4 10-10" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </svg>
  )
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: '13px', height: '13px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: '13px', height: '13px', marginLeft: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  )
}

function CheckboxIcon({ checked }: { checked: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      {checked ? <path d="m7 12 3 3 7-7" /> : null}
    </svg>
  )
}

function SortIcon({ direction }: { direction: 'asc' | 'desc' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {direction === 'asc' ? <path d="m8 14 4-4 4 4" /> : <path d="m8 10 4 4 4-4" />}
    </svg>
  )
}

function ContentIcon() {
  return <KeyIcon />
}

function statusLabel(status: string, t: (key: string) => string, i18nKey: string) {
  if (status === 'active') {
    return t(`${i18nKey}.status.active`)
  }

  if (status === 'inactive') {
    return t(`${i18nKey}.status.inactive`)
  }

  return status
}

export function RulesPage({ session, resource = 'rules' }: RulesPageProps) {
  const { t } = useTranslation()
  const i18nKey = resource
  const apiPath = `/${resource}`
  const [payload, setPayload] = useState<RulesPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [noticeTone, setNoticeTone] = useState<PageTone>('idle')
  const [noticeMessage, setNoticeMessage] = useState('')
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  })
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'updatedAt',
    direction: 'desc',
  })
  const [draft, setDraft] = useState<RuleForm>(emptyForm)
  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null)
  const [drawerItem, setDrawerItem] = useState<RuleItem | null>(null)
  const [matrix, setMatrix] = useState<PermissionMatrixPayload | null>(null)
  const [rolePermissionModal, setRolePermissionModal] = useState<RolePermissionModalState>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [openMenuState, setOpenMenuState] = useState<OpenMenuState>(null)
  const [confirmDeleteIds, setConfirmDeleteIds] = useState<string[] | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const title = t(`${i18nKey}.title`)

  const apiFetch = async (path: string, init: RequestInit = {}) => {
    const response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${session.session.token}`,
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    })

    const body = await response.json().catch(() => null)

    if (!response.ok) {
      const error = body as { code?: string; message?: string } | null
      throw new Error(error?.message || t(`${i18nKey}.error.unexpected`))
    }

    return body
  }

  const loadData = async (
    nextFilters = filters,
    nextSort = sort,
    keepSelection = false,
    showLoading = true,
    preserveNotice = false,
  ) => {
    if (showLoading) {
      setLoading(true)
    }

    try {
      const params = new URLSearchParams()
      params.set('sortBy', nextSort.key)
      params.set('sortDir', nextSort.direction)

      if (nextFilters.search.trim()) {
        params.set('search', nextFilters.search.trim())
      }

      if (nextFilters.status) {
        params.set('status', nextFilters.status)
      }

      const data = (await apiFetch(`${apiPath}?${params.toString()}`)) as RulesPayload
      setPayload(data)

      if (resource === 'permissions') {
        const nextMatrix = (await apiFetch('/permissions/matrix')) as PermissionMatrixPayload
        setMatrix(nextMatrix)
      } else {
        setMatrix(null)
      }

      if (!preserveNotice) {
        setNoticeTone('idle')
        setNoticeMessage('')
      }
      setOpenMenuState(null)

      if (!keepSelection) {
        setSelectedIds([])
      }

      return data
    } catch (error) {
      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t(`${i18nKey}.error.unexpected`))
      return null
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!noticeMessage) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setNoticeMessage('')
      setNoticeTone('idle')
    }, 3200)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [noticeMessage])

  useEffect(() => {
    void loadData(filters, sort)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, session.session.token])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters.search, filters.status, sort.key, sort.direction])

  useEffect(() => {
    if (resource === 'rules') {
      return
    }

    let active = true

    const refresh = () => {
      if (!active || document.visibilityState !== 'visible') {
        return
      }

      void loadData(filters, sort, true, false)
    }

    const intervalId = window.setInterval(refresh, 10000)

    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)

    return () => {
      active = false
      window.clearInterval(intervalId)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [filters, resource, sort, session.session.token])

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (!openMenuState) {
        return
      }

      const target = event.target as Element | null

      if (!target?.closest(`[data-menu-id="${openMenuState.id}"]`)) {
        setOpenMenuState(null)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)

    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
    }
  }, [openMenuState])

  const items = payload?.items || []
  const totalPages = Math.max(1, Math.ceil(items.length / ROWS_PER_PAGE))
  const safePage = Math.min(currentPage, totalPages)
  const pageStart = (safePage - 1) * ROWS_PER_PAGE
  const visibleItems = items.slice(pageStart, pageStart + ROWS_PER_PAGE)
  const visibleItemIds = visibleItems.map((item) => item.id)
  const paginationItems = (() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1)
    }

    const pages = new Set<number>([1, totalPages, safePage])

    for (let page = safePage - 1; page <= safePage + 1; page += 1) {
      if (page > 1 && page < totalPages) {
        pages.add(page)
      }
    }

    if (safePage <= 3) {
      pages.add(2)
      pages.add(3)
      pages.add(4)
    }

    if (safePage >= totalPages - 2) {
      pages.add(totalPages - 1)
      pages.add(totalPages - 2)
      pages.add(totalPages - 3)
    }

    return Array.from(pages).sort((left, right) => left - right).reduce<Array<number | 'ellipsis'>>(
      (accumulator, page, index, array) => {
        const previous = array[index - 1]

        if (index > 0 && previous !== undefined && page - previous > 1) {
          accumulator.push('ellipsis')
        }

        accumulator.push(page)
        return accumulator
      },
      [],
    )
  })()
  const isAllSelected =
    visibleItems.length > 0 &&
    visibleItems.every((item) => selectedIds.includes(item.id))
  const selectedCount = selectedIds.length
  const permissionMatrixRoles = resource === 'permissions' ? matrix?.roles || [] : []
  const permissionCatalogItems = resource === 'permissions' ? matrix?.permissions || items : []
  const permissionRoleRows = permissionMatrixRoles.filter((role) => {
    const search = filters.search.trim().toLowerCase()

    if (!search) {
      return true
    }

    return [role.slug, role.label, role.permissions.join(' ')]
      .join(' ')
      .toLowerCase()
      .includes(search)
  })

  const openCreate = () => {
    setDrawerItem(null)
    setDraft({
      ...emptyForm,
      status: 'active',
    })
    setDrawerMode('create')
  }

  const openView = (item: RuleItem) => {
    setDrawerItem(item)
    setDrawerMode('view')
    setOpenMenuState(null)
  }

  const openEdit = (item: RuleItem) => {
    setDrawerItem(item)
    setDraft({
      slug: item.slug,
      label: item.label,
      description: item.description,
      status: item.status,
    })
    setDrawerMode('edit')
    setOpenMenuState(null)
  }

  const closeDrawer = () => {
    setDrawerMode(null)
    setDrawerItem(null)
    setDraft(emptyForm)
  }

  useEscapeClose(Boolean(confirmDeleteIds || rolePermissionModal || drawerMode || openMenuState), () => {
    if (confirmDeleteIds) {
      setConfirmDeleteIds(null)
      return
    }

    if (rolePermissionModal) {
      setRolePermissionModal(null)
      return
    }

    if (drawerMode) {
      closeDrawer()
      return
    }

    setOpenMenuState(null)
  })

  const toggleSelection = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    )
  }

  const toggleAll = () => {
    setSelectedIds((current) => {
      const allVisibleSelected = visibleItemIds.every((id) => current.includes(id))

      if (allVisibleSelected) {
        return current.filter((id) => !visibleItemIds.includes(id))
      }

      return Array.from(new Set([...current, ...visibleItemIds]))
    })
  }

  const performDelete = async (ids: string[]) => {
    if (ids.length === 0) {
      return
    }

    setIsSaving(true)

    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiFetch(`${apiPath}/${id}`, {
            method: 'DELETE',
          }),
        ),
      )
      const failed = results.filter((result) => result.status === 'rejected').length

      setConfirmDeleteIds(null)

      if (failed === 0) {
        setNoticeTone('success')
        setNoticeMessage(t(`${i18nKey}.success.deleted`))
      } else if (failed < ids.length) {
        setNoticeTone('error')
        setNoticeMessage(t(`${i18nKey}.error.partial`, { failed, total: ids.length }))
      } else {
        setNoticeTone('error')
        setNoticeMessage(t(`${i18nKey}.error.unexpected`))
      }

      if (failed < ids.length) {
        await loadData(filters, sort, false, true, true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const performDraftSave = async () => {
    if (!drawerMode) {
      return
    }

    const currentMode = drawerMode
    const currentItemId = drawerItem?.id || null
    const payloadBody = {
      slug: draft.slug,
      label: draft.label,
      description: draft.description,
      ...(currentMode === 'edit' ? { status: draft.status } : {}),
    }

    setIsSaving(true)

    try {
      if (currentMode === 'create') {
        await apiFetch(apiPath, {
          method: 'POST',
          body: JSON.stringify(payloadBody),
        })
        setNoticeTone('success')
        setNoticeMessage(t(`${i18nKey}.success.created`))
      } else if (currentMode === 'edit' && currentItemId) {
        await apiFetch(`${apiPath}/${currentItemId}`, {
          method: 'PATCH',
          body: JSON.stringify(payloadBody),
        })
        setNoticeTone('success')
        setNoticeMessage(t(`${i18nKey}.success.updated`))
      }

      await loadData(filters, sort, false, true, true)
      closeDrawer()
    } catch (error) {
      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t(`${i18nKey}.error.unexpected`))
    } finally {
      setIsSaving(false)
    }
  }

  const submitDraft = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await performDraftSave()
  }

  const submitSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setCurrentPage(1)
    await loadData(filters, sort)
  }

  const resetSearch = async () => {
    const next = { search: '', status: '' }
    setCurrentPage(1)
    setFilters(next)
    await loadData(next, sort)
  }

  const toggleSort = async (key: SortKey) => {
    const nextSort: { key: SortKey; direction: 'asc' | 'desc' } =
      sort.key === key
        ? {
            key,
            direction: sort.direction === 'asc' ? 'desc' : 'asc',
          }
        : {
            key,
            direction: 'asc' as const,
          }

    setSort(nextSort)
    await loadData(filters, nextSort, true)
  }

  const submitBulkDelete = () => {
    requestDelete(selectedIds)
  }

  const requestDelete = (ids: string[]) => {
    setConfirmDeleteIds(ids)
  }

  const setRowMenu = (id: string, buttonElement: HTMLButtonElement) => {
    const rect = buttonElement.getBoundingClientRect()
    const tableWrap = buttonElement.closest('.admin-table-wrap')
    const wrapRect = tableWrap ? tableWrap.getBoundingClientRect() : null
    const spaceBelow = wrapRect ? wrapRect.bottom - rect.bottom : window.innerHeight - rect.bottom
    const spaceAbove = wrapRect ? rect.top - wrapRect.top : rect.top
    const menuEstimate = 190
    const direction: MenuDirection =
      spaceBelow < menuEstimate && spaceAbove > spaceBelow ? 'up' : 'down'

    const coords = {
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
    }

    setOpenMenuState((current) => {
      if (current?.id === id) {
        return null
      }
      return { id, direction, coords }
    })
  }

  const goToPage = (nextPage: number) => {
    const boundedPage = Math.min(Math.max(nextPage, 1), totalPages)
    setCurrentPage(boundedPage)
    setOpenMenuState(null)
  }

  const renderSortHeader = (key: SortKey, label: string) => {
    const active = sort.key === key
    const direction = active ? sort.direction : 'asc'

    return (
      <button
        type="button"
        className={`admin-sort-button${active ? ' admin-sort-button--active' : ''}`}
        onClick={() => void toggleSort(key)}
        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        <SortIcon direction={direction} />
      </button>
    )
  }

  const drawerTitle =
    drawerMode === 'create'
      ? t(`${i18nKey}.drawer.newTitle`)
      : drawerMode === 'edit'
        ? t(`${i18nKey}.drawer.editTitle`)
        : t(`${i18nKey}.drawer.viewTitle`)

  const drawerEyebrow =
    drawerMode === 'create'
      ? ''
      : drawerMode === 'edit'
        ? ''
        : ''

  const openRolePermissionModal = (role: PermissionMatrixRole) => {
    setRolePermissionModal({
      role,
      selectedPermissionSlugs: role.permissions,
    })
  }

  const toggleRolePermissionDraft = (permissionSlug: string) => {
    setRolePermissionModal((current) => {
      if (!current) {
        return current
      }

      const selectedPermissionSlugs = current.selectedPermissionSlugs.includes(permissionSlug)
        ? current.selectedPermissionSlugs.filter((slug) => slug !== permissionSlug)
        : [...current.selectedPermissionSlugs, permissionSlug]

      return {
        ...current,
        selectedPermissionSlugs,
      }
    })
  }

  const saveRolePermissionModal = async () => {
    if (!rolePermissionModal || isSaving) {
      return
    }

    const { role, selectedPermissionSlugs } = rolePermissionModal
    setIsSaving(true)

    try {
      await apiFetch(`/permissions/roles/${encodeURIComponent(role.slug)}`, {
        method: 'PATCH',
        body: JSON.stringify({ permissionSlugs: selectedPermissionSlugs }),
      })
      await loadData(filters, sort, true, false, true)
      setRolePermissionModal(null)
      setNoticeTone('success')
      setNoticeMessage(t('permissions.success.updated'))
    } catch (error) {
      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t('permissions.error.unexpected'))
    } finally {
      setIsSaving(false)
    }
  }

  if (resource === 'permissions') {
    return (
      <div className="admin-content-shell admin-content-shell--full">
        <header className="admin-content-header">
          <div className="admin-content-header__icon">
            <ContentIcon />
          </div>
          <div className="admin-content-header__copy">
            <h2>{title}</h2>
          </div>
        </header>

        <section className="admin-panel admin-panel--team admin-panel--permissions">
          <div className="admin-panel__header admin-panel__header--team">
            <div>
              <h3>{t('permissions.list.label')}</h3>
              <p>{t('permissions.list.description')}</p>
            </div>
          </div>

          <form className="admin-search" onSubmit={submitSearch}>
            <label className="admin-search__field">
              <span>{t('permissions.filters.search')}</span>
              <div className="admin-search__input">
                <SearchIcon />
                <input
                  type="search"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((current) => ({ ...current, search: event.target.value }))
                  }
                  placeholder={t('permissions.filters.searchPlaceholder')}
                />
              </div>
            </label>

            <div className="admin-search__field" />
            <div className="admin-search__field" />

            <div className="admin-search__actions">
              <button type="submit" className="admin-action-button">
                <SearchIcon />
                {t('permissions.actions.search')}
              </button>
              <button type="button" className="admin-link-button" onClick={() => void resetSearch()}>
                <XIcon />
                {t('permissions.actions.clear')}
              </button>
            </div>
          </form>

          <div className="admin-table-wrap admin-table-wrap--rules">
            <table className="admin-table admin-table--rules admin-table--permissions">
              <thead>
                <tr>
                  <th>{t('permissions.roleTable.role')}</th>
                  <th>{t('permissions.roleTable.permissions')}</th>
                  <th>{t('permissions.roleTable.rules')}</th>
                </tr>
              </thead>
              <tbody>
                {!loading && permissionRoleRows.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <div className="admin-empty-state">
                        <strong>{t('permissions.empty.title')}</strong>
                        <p>{t('permissions.empty.description')}</p>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {permissionRoleRows.map((role) => (
                    <tr key={role.slug}>
                      <td>
                        <button
                          type="button"
                          className="admin-role-permission-button"
                          onClick={() => openRolePermissionModal(role)}
                        >
                          <KeyIcon />
                          <span>
                            <strong>{catalogLabel(role.slug, t)}</strong>
                            <small>{role.slug}</small>
                          </span>
                        </button>
                      </td>
                      <td>
                        <strong>{role.permissions.length}</strong>
                        <small>{t('permissions.roleTable.permissionsHint')}</small>
                      </td>
                      <td>
                        <strong>{role.rules.length}</strong>
                        <small>{t('permissions.roleTable.rulesHint')}</small>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination" aria-label={t('permissions.pagination.label')}>
            <div className="admin-pagination__summary">
              <span>{t('permissions.roleTable.summary', { count: permissionRoleRows.length })}</span>
              <strong>{t('permissions.roleTable.catalog', { count: permissionCatalogItems.length })}</strong>
            </div>
          </div>
        </section>

        {rolePermissionModal ? (
          <div className="admin-modal" role="presentation">
            <div className="admin-modal__surface admin-modal__surface--permissions" role="dialog" aria-modal="true">
              <button
                type="button"
                className="admin-icon-button admin-icon-button--bare admin-modal__close"
                onClick={() => setRolePermissionModal(null)}
                aria-label={t('common.close')}
              >
                <XIcon />
              </button>
              <p className="admin-modal__eyebrow">
                {t('permissions.modal.assignTitle', {
                  role: catalogLabel(rolePermissionModal.role.slug, t),
                })}
              </p>
              <div className="admin-permission-role-modal">
                {permissionCatalogItems.map((permission) => {
                  const checked = rolePermissionModal.selectedPermissionSlugs.includes(permission.slug)

                  return (
                    <button
                      key={permission.slug}
                      type="button"
                      className={`admin-permission-role-option${checked ? ' admin-permission-role-option--checked' : ''}`}
                      onClick={() => toggleRolePermissionDraft(permission.slug)}
                      aria-pressed={checked}
                    >
                      <CheckboxIcon checked={checked} />
                      <span>{catalogLabel(permission.slug, t)}</span>
                    </button>
                  )
                })}
              </div>
              <div className="admin-modal__actions">
                <button type="button" className="admin-link-button" onClick={() => setRolePermissionModal(null)}>
                  <XIcon />
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="admin-action-button"
                  onClick={() => void saveRolePermissionModal()}
                  disabled={isSaving}
                >
                  <CheckIcon />
                  {isSaving ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {drawerMode ? (
          <div className="admin-modal" role="presentation">
            <div className="admin-modal__surface" role="dialog" aria-modal="true">
              <div className="admin-drawer__header">
                <div>
                  <h3>{drawerTitle}</h3>
                </div>
                <button
                  type="button"
                  className="admin-icon-button admin-icon-button--bare"
                  onClick={closeDrawer}
                  aria-label={t('common.cancel')}
                >
                  <XIcon />
                </button>
              </div>

              <form className="admin-form" onSubmit={submitDraft}>
                <label>
                  <span>{t('permissions.form.label')}</span>
                  <input
                    type="text"
                    value={draft.label}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, label: event.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  <span>{t('permissions.form.slug')}</span>
                  <input
                    type="text"
                    value={draft.slug}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, slug: event.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  <span>{t('permissions.form.description')}</span>
                  <textarea
                    value={draft.description}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, description: event.target.value }))
                    }
                    rows={4}
                    required
                  />
                </label>

                {drawerMode === 'edit' ? (
                  <label>
                    <span>{t('permissions.form.status')}</span>
                    <div className="admin-status-switch" data-state={draft.status}>
                      <label className="admin-status-switch__control">
                        <input
                          type="checkbox"
                          checked={draft.status === 'active'}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              status: event.target.checked ? 'active' : 'inactive',
                            }))
                          }
                          aria-label={t('permissions.form.status')}
                        />
                        <span className="admin-status-switch__track">
                          <span className="admin-status-switch__thumb" />
                        </span>
                      </label>
                    </div>
                  </label>
                ) : null}

                <div className="admin-modal__actions">
                  <button type="button" className="admin-link-button" onClick={closeDrawer}>
                    <XIcon />
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="admin-action-button" disabled={isSaving}>
                    <CheckIcon />
                    {isSaving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {confirmDeleteIds ? (
          <div className="admin-modal" role="presentation">
            <div className="admin-modal__surface admin-modal__surface--confirm" role="dialog" aria-modal="true">
              <button
                type="button"
                className="admin-icon-button admin-icon-button--bare admin-modal__close"
                onClick={() => {
                  setConfirmDeleteIds(null)
                }}
                aria-label={t('common.close')}
              >
                <XIcon />
              </button>
              <p className="admin-modal__eyebrow">{t('permissions.modal.deleteTitle')}</p>
              {confirmDeleteIds.length > 1 ? (
                <p>{t('permissions.modal.bulkDeleteText', { count: confirmDeleteIds.length })}</p>
              ) : null}
              <div className="admin-modal__actions">
                <button
                  type="button"
                  className="admin-link-button"
                  onClick={() => {
                    setConfirmDeleteIds(null)
                  }}
                  aria-label={t('common.cancel')}
                >
                  <XIcon />
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="admin-action-button admin-action-button--danger"
                  onClick={() => void performDelete(confirmDeleteIds)}
                  disabled={isSaving}
                >
                  <DeleteIcon />
                  {isSaving ? t('common.loading') : t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {noticeMessage ? (
          <div className="admin-toast-stack" aria-live="polite" aria-atomic="true">
            <div className={`admin-toast admin-toast--${noticeTone}`} role="status">
              <div className="admin-toast__content">
                <strong>{noticeMessage}</strong>
              </div>
              <button
                type="button"
                className="admin-icon-button admin-icon-button--bare"
                onClick={() => {
                  setNoticeMessage('')
                  setNoticeTone('idle')
                }}
                aria-label={t('common.close')}
              >
                <XIcon />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="admin-content-shell admin-content-shell--full">
      <header className="admin-content-header">
        <div className="admin-content-header__icon">
          <ContentIcon />
        </div>
        <div className="admin-content-header__copy">
          <h2>{title}</h2>
        </div>
      </header>

      <section className="admin-panel admin-panel--team">
        <div className="admin-panel__header admin-panel__header--team">
          <div>
            <h3>{t(`${i18nKey}.list.label`)}</h3>
            <p>{t(`${i18nKey}.list.description`)}</p>
          </div>
          <button type="button" className="admin-action-button" onClick={openCreate}>
            <PlusIcon />
            {t(`${i18nKey}.actions.create`)}
          </button>
        </div>

        <form className="admin-search" onSubmit={submitSearch}>
          <label className="admin-search__field">
            <span>{t(`${i18nKey}.filters.search`)}</span>
            <div className="admin-search__input">
              <SearchIcon />
              <input
                type="search"
                value={filters.search}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, search: event.target.value }))
                }
                placeholder={t(`${i18nKey}.filters.searchPlaceholder`)}
              />
            </div>
          </label>

          <label className="admin-search__field">
            <span>{t(`${i18nKey}.filters.status`)}</span>
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({ ...current, status: event.target.value }))
              }
            >
              <option value="">{t(`${i18nKey}.filters.any`)}</option>
              <option value="active">{t(`${i18nKey}.status.active`)}</option>
              <option value="inactive">{t(`${i18nKey}.status.inactive`)}</option>
            </select>
          </label>

          <div className="admin-search__field" />

          <div className="admin-search__actions">
            <button type="submit" className="admin-action-button">
              <SearchIcon />
              {t(`${i18nKey}.actions.search`)}
            </button>
            <button type="button" className="admin-link-button" onClick={() => void resetSearch()}>
              <XIcon />
              {t(`${i18nKey}.actions.clear`)}
            </button>
          </div>
        </form>

        {selectedCount > 0 ? (
          <div className="admin-bulkbar">
            <div className="admin-bulkbar__copy">
              <strong>{t(`${i18nKey}.bulk.selected`, { count: selectedCount })}</strong>
              <span>{t(`${i18nKey}.bulk.helper`)}</span>
            </div>
            <div className="admin-bulkbar__actions">
              <button
                type="button"
                className="admin-action-button"
                onClick={submitBulkDelete}
                disabled={isSaving}
              >
                <TrashIcon />
                {t(`${i18nKey}.bulk.moveToTrash`)}
              </button>
              <button type="button" className="admin-link-button" onClick={() => setSelectedIds([])}>
                <XIcon />
                {t(`${i18nKey}.bulk.clearSelection`)}
              </button>
            </div>
          </div>
        ) : null}

        <div className="admin-table-wrap admin-table-wrap--rules">
          <table className="admin-table admin-table--rules">
            <thead>
              <tr>
                <th className="admin-table__checkbox">
                  <button
                    type="button"
                    className="admin-checkbox-button"
                    onClick={toggleAll}
                    aria-label={t(`${i18nKey}.table.selectAll`)}
                  >
                    <CheckboxIcon checked={isAllSelected} />
                  </button>
                </th>
                <th>{renderSortHeader('label', t(`${i18nKey}.table.label`))}</th>
                <th>{renderSortHeader('slug', t(`${i18nKey}.table.slug`))}</th>
                <th>{renderSortHeader('status', t(`${i18nKey}.table.status`))}</th>
                <th>{renderSortHeader('updatedAt', t(`${i18nKey}.table.updatedAt`))}</th>
                <th>{t(`${i18nKey}.table.actions`)}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="admin-empty-state">
                      <strong>{t(`${i18nKey}.empty.title`)}</strong>
                      <p>{t(`${i18nKey}.empty.description`)}</p>
                    </div>
                  </td>
                </tr>
              ) : null}

              {visibleItems.map((item) => {
                const checked = selectedIds.includes(item.id)

                return (
                  <tr key={item.id} className={checked ? 'admin-table__row--selected' : ''}>
                    <td className="admin-table__checkbox">
                      <button
                        type="button"
                        className="admin-checkbox-button"
                        onClick={() => toggleSelection(item.id)}
                        aria-label={t(`${i18nKey}.table.selectRow`, { label: item.label })}
                      >
                        <CheckboxIcon checked={checked} />
                      </button>
                    </td>
                    <td>
                      <strong>{item.label}</strong>
                      <small>{item.description}</small>
                    </td>
                    <td>{catalogLabel(item.slug, t)}</td>
                    <td>
                      <span className={`admin-status admin-status--${item.status}`}>
                        {statusLabel(item.status, t, i18nKey)}
                      </span>
                    </td>
                    <td>{formatDateTime(item.updatedAt)}</td>
                    <td>
                      <div className="admin-row-actions" data-menu-id={item.id}>
                        <div className="admin-row-menu">
                          <button
                            data-menu-toggle
                            type="button"
                            className="admin-icon-button"
                            onClick={(event) => setRowMenu(item.id, event.currentTarget)}
                            aria-expanded={openMenuState?.id === item.id}
                            aria-haspopup="menu"
                            title={t(`${i18nKey}.actions.more`)}
                          >
                            <MoreIcon />
                          </button>
                          {openMenuState?.id === item.id ? createPortal(
                             <div
                               data-menu-id={openMenuState.id}
                               className={`admin-row-menu__panel admin-row-menu__panel--${openMenuState.direction}`}
                               role="menu"
                               style={{
                                 position: 'fixed',
                                 top: openMenuState.direction === 'down' ? `${openMenuState.coords.bottom + 8}px` : 'auto',
                                 bottom: openMenuState.direction === 'up' ? `${window.innerHeight - openMenuState.coords.top + 8}px` : 'auto',
                                 left: openMenuState.coords.left < window.innerWidth / 2 ? `${openMenuState.coords.left}px` : 'auto',
                                 right: openMenuState.coords.left < window.innerWidth / 2 ? 'auto' : `${window.innerWidth - openMenuState.coords.right}px`,
                                 transform: 'none',
                                 zIndex: 9999,
                               }}
                             >
                               <button
                                 type="button"
                                 className="admin-row-menu__item"
                                 onClick={() => openView(item)}
                               >
                                 <EyeIcon />
                                 {t(`${i18nKey}.actions.view`)}
                               </button>
                               <button
                                 type="button"
                                 className="admin-row-menu__item"
                                 onClick={() => openEdit(item)}
                               >
                                 <PencilIcon />
                                 {t(`${i18nKey}.actions.edit`)}
                               </button>
                               <button
                                 type="button"
                                 className="admin-row-menu__item admin-row-menu__item--danger"
                                 onClick={() => {
                                   setOpenMenuState(null)
                                   requestDelete([item.id])
                                 }}
                               >
                                 <TrashIcon />
                                 {t(`${i18nKey}.actions.moveToTrash`)}
                               </button>
                             </div>,
                             document.body
                           ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="admin-pagination" aria-label={t(`${i18nKey}.pagination.label`)}>
          <div className="admin-pagination__summary">
            <span>{t(`${i18nKey}.pagination.page`, { current: safePage, total: totalPages })}</span>
            <strong>
              {t(`${i18nKey}.pagination.count`, {
                current: visibleItems.length,
                total: items.length,
              })}
            </strong>
          </div>

          <div className="admin-pagination__nav">
            <div className="admin-pagination__actions">
              <button
                type="button"
                className="admin-link-button"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
              >
                <ChevronLeftIcon />
                {t(`${i18nKey}.pagination.previous`)}
              </button>
            </div>

            <div className="admin-pagination__pages" aria-label={t(`${i18nKey}.pagination.label`)}>
              {paginationItems.map((item, index) =>
                item === 'ellipsis' ? (
                  <span key={`ellipsis-${index}`} className="admin-pagination__ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`admin-pagination__page${item === safePage ? ' admin-pagination__page--active' : ''}`}
                    onClick={() => goToPage(item)}
                    aria-current={item === safePage ? 'page' : undefined}
                  >
                    {item}
                  </button>
                ),
              )}
            </div>

            <div className="admin-pagination__actions admin-pagination__actions--end">
              <button
                type="button"
                className="admin-link-button"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
              >
                {t(`${i18nKey}.pagination.next`)}
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {drawerMode ? (
        drawerMode === 'create' ? (
          <div className="admin-modal" role="presentation">
            <div className="admin-modal__surface" role="dialog" aria-modal="true">
              <div className="admin-drawer__header">
                <div>
                  <h3>{drawerTitle}</h3>
                </div>
                <button
                  type="button"
                  className="admin-icon-button admin-icon-button--bare"
                  onClick={closeDrawer}
                  aria-label={t('common.cancel')}
                >
                  <XIcon />
                </button>
              </div>

              <form className="admin-form" onSubmit={submitDraft}>
                <label>
                  <span>{t(`${i18nKey}.form.label`)}</span>
                  <input
                    type="text"
                    value={draft.label}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, label: event.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  <span>{t(`${i18nKey}.form.slug`)}</span>
                  <input
                    type="text"
                    value={draft.slug}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, slug: event.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  <span>{t(`${i18nKey}.form.description`)}</span>
                  <textarea
                    value={draft.description}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, description: event.target.value }))
                    }
                    rows={4}
                    required
                  />
                </label>

                <div className="admin-modal__actions">
                  <button type="button" className="admin-link-button" onClick={closeDrawer}>
                    <XIcon />
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="admin-action-button" disabled={isSaving}>
                    <CheckIcon />
                    {isSaving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className={drawerMode === 'view' ? 'admin-modal' : 'admin-drawer'} role="presentation">
            <div
              className={
                drawerMode === 'view'
                  ? 'admin-modal__surface admin-modal__surface--view'
                  : `admin-drawer__surface${drawerMode === 'edit' ? ' admin-drawer__surface--topform' : ''}`
              }
              role="dialog"
              aria-modal="true"
            >
              <div className="admin-drawer__header">
                <div>
                  {drawerEyebrow ? (
                    <p className="admin-drawer__eyebrow">{drawerEyebrow}</p>
                  ) : null}
                  <h3>{drawerMode === 'view' ? title : drawerTitle}</h3>
                </div>
                <button
                  type="button"
                  className="admin-icon-button admin-icon-button--bare"
                  onClick={closeDrawer}
                  aria-label={t('common.cancel')}
                >
                  <XIcon />
                </button>
              </div>

              {drawerMode === 'view' && drawerItem ? (
                <div className="admin-modal__view">
                  <div className="admin-modal__view-grid">
                    <div className="admin-view-card">
                      <span>{t(`${i18nKey}.form.label`)}</span>
                      <strong>{drawerItem.label}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t(`${i18nKey}.form.slug`)}</span>
                      <strong>{catalogLabel(drawerItem.slug, t)}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t(`${i18nKey}.form.status`)}</span>
                      <strong>{statusLabel(drawerItem.status, t, i18nKey)}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t(`${i18nKey}.detail.roles`)}</span>
                      <strong>{drawerItem.rolesCount}</strong>
                    </div>
                  </div>

                  <div className="admin-view-card admin-view-card--wide">
                    <span>{t(`${i18nKey}.form.description`)}</span>
                    <strong>{drawerItem.description}</strong>
                  </div>

                  {drawerItem.deletedBy ? (
                    <div className="admin-view-card admin-view-card--wide">
                      <span>{t(`${i18nKey}.detail.deletedBy`)}</span>
                      <strong>{drawerItem.deletedBy.name}</strong>
                    </div>
                  ) : null}

                  <div className="admin-modal__actions admin-modal__actions--view">
                    <button type="button" className="admin-link-button" onClick={closeDrawer}>
                      <XIcon />
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <form className="admin-form" onSubmit={submitDraft}>
                  <label>
                    <span>{t(`${i18nKey}.form.label`)}</span>
                    <input
                      type="text"
                      value={draft.label}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, label: event.target.value }))
                      }
                      required
                    />
                  </label>

                  <label>
                    <span>{t(`${i18nKey}.form.slug`)}</span>
                    <input
                      type="text"
                      value={draft.slug}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, slug: event.target.value }))
                      }
                      required
                    />
                  </label>

                  <label>
                    <span>{t(`${i18nKey}.form.description`)}</span>
                    <textarea
                      value={draft.description}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, description: event.target.value }))
                      }
                      rows={4}
                      required
                    />
                  </label>

                  <label>
                    <span>{t(`${i18nKey}.form.status`)}</span>
                    <div className="admin-status-switch" data-state={draft.status}>
                      <label className="admin-status-switch__control">
                        <input
                          type="checkbox"
                          checked={draft.status === 'active'}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              status: event.target.checked ? 'active' : 'inactive',
                            }))
                          }
                          aria-label={t(`${i18nKey}.form.status`)}
                        />
                        <span className="admin-status-switch__track">
                          <span className="admin-status-switch__thumb" />
                        </span>
                      </label>
                    </div>
                  </label>

                  <div className="admin-drawer__actions">
                    <button type="button" className="admin-link-button" onClick={closeDrawer}>
                      <XIcon />
                      {t('common.cancel')}
                    </button>
                    <button type="submit" className="admin-action-button" disabled={isSaving}>
                      <CheckIcon />
                      {isSaving ? t('common.loading') : t('common.save')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )
      ) : null}

      {confirmDeleteIds ? (
        <div className="admin-modal" role="presentation">
          <div className="admin-modal__surface admin-modal__surface--confirm" role="dialog" aria-modal="true">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              onClick={() => {
                setConfirmDeleteIds(null)
              }}
              aria-label={t('common.close')}
            >
              <XIcon />
            </button>
            <p className="admin-modal__eyebrow">{t(`${i18nKey}.modal.deleteTitle`)}</p>
            {confirmDeleteIds.length > 1 ? (
              <p>{t(`${i18nKey}.modal.bulkDeleteText`, { count: confirmDeleteIds.length })}</p>
            ) : null}
            <div className="admin-modal__actions">
              <button
                type="button"
                className="admin-link-button"
                onClick={() => {
                  setConfirmDeleteIds(null)
                }}
                aria-label={t('common.cancel')}
              >
                <XIcon />
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-action-button admin-action-button--danger"
                onClick={() => void performDelete(confirmDeleteIds)}
                disabled={isSaving}
              >
                <DeleteIcon />
                {isSaving ? t('common.loading') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {noticeMessage ? (
        <div className="admin-toast-stack" aria-live="polite" aria-atomic="true">
          <div className={`admin-toast admin-toast--${noticeTone}`} role="status">
            <div className="admin-toast__content">
              <strong>{noticeMessage}</strong>
            </div>
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare"
              onClick={() => {
                setNoticeMessage('')
                setNoticeTone('idle')
              }}
              aria-label={t('common.close')}
            >
              <XIcon />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
